/**
 * Search actions file
 *
 * @author Matthew Rawson @ 03/01/2018 15:34
 */
import {search as searchService} from "../../services/search"
import {DEFAULT_SEARCH_LIMIT} from "../../constants/search"
import {isSearchTermValid} from "../../helpers/search/validation"


/**
 * Constants
 */
export const NEW_URL_QUERY_TERM = "NEW_URL_QUERY_TERM"
export const NEW_URL_LAT_LNG = "NEW_URL_LAT_LNG"
export const NEW_URL_RADIUS = "NEW_URL_RADIUS"
export const NEW_URL_BOUNDING_BOX = "NEW_URL_BOUNDING_BOX"
export const PEND_LOAD_RESULTS = "PEND_LOAD_RESULTS"
export const LOAD_RESULTS = "LOAD_RESULTS"
export const UPDATE_RESULTS = "UPDATE_RESULTS"
export const CLEAR_RESULTS = "CLEAR_RESULTS"
export const SET_SEARCH_RESULTS_INPUT = "SET_SEARCH_RESULTS_INPUT"
export const CLEAR_PEND = "CLEAR_PEND"


/**
 * Action creators
 */
export const newUrlQueryTerm = (q) => ({
  type: NEW_URL_QUERY_TERM,
  search: {
    q
  }
})

export const newUrlLatLng = (lat, lng) => ({
  type: NEW_URL_LAT_LNG,
  search: {
    lat,
    lng
  }
})

export const newUrlRadius = (radius) => ({
  type: NEW_URL_RADIUS,
  search: {
    radius
  }
})

export const newUrlBoundingBox = (boundingBox) => ({
  type: NEW_URL_BOUNDING_BOX,
  search: {
    boundingBox
  }
})

export const pendLoadResults = () => ({
  type: PEND_LOAD_RESULTS
})

export const loadResults = (load) => ({
  type: LOAD_RESULTS,
  search: {
    load
  }
})

export const updateResults = (q, lat, lng, radius, boundingBox, results) => ({
  type: UPDATE_RESULTS,
  search: {
    lastSuccessfulQ: q,
    lastSuccessfulLat: lat,
    lastSuccessfulLng: lng,
    lastSuccessfulRadius: radius,
    lastSuccessfulBoundingBox: boundingBox,
    results
  }
})

export const clearResults = () => ({
  type: CLEAR_RESULTS
})

export const setSearchResultsInput = (input) => ({
  type: SET_SEARCH_RESULTS_INPUT,
  search: {
    input
  }
})

export const clearPend = () => ({
  type: CLEAR_PEND
})


/**
 *
 * Thunk action creators
 *
 */

/**
 * newSearch: searchService returns the searchTerm q, using this along with latestSearchTerm we can only
 * dispatch and update the search results when they are the last results
 * requested.
 *
 * @author Matthew Rawson @ 08/01/2018 16:58
 * @param searchTerm
 * @param options
 * @returns {function(*=)}
 */
export const newSearch = (
  searchTerm,
  geoParams = null,
  options = {
    limit: DEFAULT_SEARCH_LIMIT
  }) => {
  return (dispatch) => {
    let load = Promise.resolve()
    const params = {limit: options.limit, q: searchTerm}
    const isGeoSearch = geoParams
    if (isGeoSearch) {
      const {lat, lng, radius, boundingBox} = geoParams
      params.geo = 1
      params.slim = 1
      if (geoParams.boundingBox) {
        params.insideBoundingBox = boundingBox
      } else {
        params.lat = lat
        params.lng = lng
        params.radius = radius
      }
    }
    /** Fetch or clear results based on searchTerm validation */
    if (isSearchTermValid(searchTerm, !isGeoSearch)) {
      load = searchService(params).then((results) => {
        dispatch(
          updateResults(
            results.data.q,
            results.data.lat,
            results.data.lng,
            results.data.radius,
            results.data.boundingBox,
            results
          )
        )
      })
      dispatch(loadResults(load))
    } else {
      dispatch(clearResults())
    }

    /** Regardless of whether the search term is valid update the state to what the latest url query state */
    dispatch(newUrlQueryTerm(searchTerm))
    if (isGeoSearch) {
      const {lat, lng, radius, boundingBox} = geoParams
      dispatch(newUrlLatLng(lat, lng))
      dispatch(newUrlRadius(radius))
      dispatch(newUrlBoundingBox(boundingBox))
    }
    return load
  }
}


export const retryLastSearch = () => {
  return (dispatch, getState) => {
    const {q, lat, lng, radius, boundingBox, options} = getState().search
    return dispatch(newSearch(q, {lat, lng, radius, boundingBox}, {...options, delay: 0}))
  }
}


/**
 * Export all
 */
export default {
  LOAD_RESULTS,
  UPDATE_RESULTS,
  newSearch,
  retryLastSearch
}
